import { Cursor } from 'loro-crdt';
import { createId } from '@paralleldrive/cuid2';
import { b64, unb64 } from './base64';
/** Collaborative comment threads anchored to text ranges via Loro cursors. */
export class Comments {
    doc;
    textId;
    listId;
    events;
    /** @param options - Document, text-container, thread-list, and event options. */
    constructor(options) {
        this.doc = options.doc;
        this.textId = options.textContainer;
        this.listId = options.listContainer ?? `${options.textContainer}:comments`;
        this.events = options.events;
    }
    /**
     * Adds a root comment (anchored to a text range or a named target) or a
     * reply. Replies attach to the thread root and inherit its anchor; replying
     * to a resolved thread reopens it.
     *
     * @param input - Comment body, author, anchor or parent, and optional mentions.
     * @returns The resolved comment.
     */
    add(input) {
        // before:comment:added — handlers may rewrite input or throw to cancel.
        const data = this.events
            ? this.events.emit('before:comment:added', { input }).input
            : input;
        const list = this.rawList();
        const items = list.toArray();
        const base = {
            id: createId(),
            body: data.body,
            author: data.author,
            mentions: [
                ...new Set((data.mentions ?? [])
                    .map((m) => m.trim().toLowerCase())
                    .filter(Boolean)),
            ],
            createdAt: Date.now(),
            resolved: false,
        };
        let stored;
        if ('parentId' in data) {
            const parent = items.find((c) => c.id === data.parentId);
            if (!parent)
                throw new Error('no such thread');
            const rootId = parent.parentId ?? parent.id;
            const rootIndex = items.findIndex((c) => c.id === rootId);
            const root = items[rootIndex];
            if (!root)
                throw new Error('no such thread');
            stored = {
                ...base,
                ...(root.startCursor && { startCursor: root.startCursor }),
                ...(root.endCursor && { endCursor: root.endCursor }),
                ...(root.target !== undefined && { target: root.target }),
                parentId: root.id,
            };
            if (root.resolved) {
                const { resolvedBy: _by, resolvedAt: _at, ...open } = root;
                list.set(rootIndex, { ...open, resolved: false });
            }
        }
        else if ('target' in data) {
            stored = { ...base, target: data.target };
        }
        else {
            const text = this.doc.getText(this.textId);
            const startCursor = text.getCursor(data.start, -1);
            const endCursor = text.getCursor(data.end, 1);
            if (!startCursor || !endCursor) {
                throw new Error('cannot anchor comment to range');
            }
            stored = {
                ...base,
                startCursor: b64(startCursor.encode()),
                endCursor: b64(endCursor.encode()),
            };
        }
        list.push(stored);
        this.doc.commit();
        const comment = this.resolveOne(stored);
        this.events?.notify('comment:added', { comment });
        return comment;
    }
    /**
     * Marks a comment thread as resolved.
     *
     * @param id - Comment identifier to mark resolved.
     * @param by - Who resolved it.
     */
    resolve(id, by) {
        const list = this.rawList();
        const items = list.toArray();
        const index = items.findIndex((c) => c.id === id);
        if (index === -1)
            return;
        list.set(index, {
            ...items[index],
            resolved: true,
            ...(by !== undefined && { resolvedBy: by }),
            resolvedAt: Date.now(),
        });
        this.doc.commit();
        this.events?.notify('comment:resolved', { id });
    }
    /**
     * Reopens a resolved comment thread.
     *
     * @param id - Comment identifier to reopen.
     */
    reopen(id) {
        const list = this.rawList();
        const items = list.toArray();
        const target = items.find((c) => c.id === id);
        if (!target)
            return;
        const rootId = target.parentId ?? target.id;
        const index = items.findIndex((c) => c.id === rootId);
        const comment = items[index];
        if (!comment || !comment.resolved)
            return;
        const { resolvedBy: _by, resolvedAt: _at, ...open } = comment;
        list.set(index, { ...open, resolved: false });
        this.doc.commit();
        this.events?.notify('comment:reopened', { id: rootId });
    }
    /**
     * Lists comments.
     *
     * @returns All resolved cursor records in insertion order.
     */
    list() {
        return this.rawList().toArray().map((c) => this.resolveOne(c));
    }
    /** Loro container id of the comment list. */
    get containerId() {
        return this.listId;
    }
    /** Raw stored records, snapshotted for a forged-write authorization check on a fork. */
    snapshot() {
        return this.rawList().toArray();
    }
    rawList() {
        return this.doc.getMovableList(this.listId);
    }
    resolveOne(c) {
        const cursor = (encoded) => encoded
            ? (this.doc.getCursorPos(Cursor.decode(unb64(encoded)))?.offset ?? null)
            : null;
        return {
            id: c.id,
            start: cursor(c.startCursor),
            end: cursor(c.endCursor),
            target: c.target ?? null,
            body: c.body,
            author: c.author,
            mentions: c.mentions ?? [],
            createdAt: c.createdAt,
            resolved: c.resolved,
            resolvedBy: c.resolvedBy ?? null,
            resolvedAt: c.resolvedAt ?? null,
            parentId: c.parentId,
        };
    }
}
//# sourceMappingURL=comments.js.map